import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useMutation } from "react-query";
import client from "../../../API"; // Adjust path as needed
import {
  formatDateToYYYYMMDD,
  parseLocalFloat,
  payoutStatuses
} from "../../../Utils/constants";

const methodOptions = [
  "Amex Platinum",
  "Chase Ink",
  "Prosperity Cash",
  "Blitz Chase Account"
];

export default function PayoutsDialog({ open, onClose, payoutInfo }) {
  const [poNumber, setPONumber] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [amount, setAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [status, setStatus] = useState("");
  const [payoutDate, setPayoutDate] = useState("");
  const [blitzPay, setBlitzPay] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState("");

  // initialize form when payoutInfo changes
  useEffect(() => {
    if (payoutInfo) {
      setPONumber(payoutInfo.po_number);
      setCreatorName(payoutInfo.creatorName || "");
      setAmount(payoutInfo.amount?.toFixed(2) || "");
      setInterestRate(payoutInfo.interest_rate?.toFixed(2) || "");
      setStatus(payoutInfo.status);
      setPayoutDate(payoutInfo.payout_date);
      setBlitzPay(!!payoutInfo.blitzpay);
      setPayoutMethod(payoutInfo.payout_method || "");
    }
  }, [payoutInfo]);

  // mutation for editing payout
  const mutation = useMutation(
    data => client.payouts.editAdmin(data),
    {
      onSuccess: () => {
        onClose(true);
      },
      onError: err => {
        alert(err?.response?.data?.error || err.message);
      }
    }
  );

  const handleSubmit = () => {
    const submissionData = {
      id: payoutInfo.id,
      amount: parseLocalFloat(amount),
      interest_rate: parseLocalFloat(interestRate),
      status,
      blitzPay,
      payout_method: payoutMethod
    };
    mutation.mutate(submissionData);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>Edit Payout</DialogTitle>
      <DialogContent>
        <TextField
          label="PO Number"
          fullWidth
          margin="dense"
          value={poNumber}
          disabled
        />
        <TextField
          label="Creator Name"
          fullWidth
          margin="dense"
          value={creatorName}
          InputProps={{ readOnly: true }}
        />
        <TextField
          label="Amount Due"
          type="number"
          fullWidth
          margin="dense"
          value={amount}
          onChange={e => setAmount(e.target.value)}
        />
        <TextField
          label="Interest Rate (%)"
          type="number"
          fullWidth
          margin="dense"
          value={interestRate}
          onChange={e => setInterestRate(e.target.value)}
        />
        <TextField
          label="Final Amount"
          type="number"
          fullWidth
          margin="dense"
          value={(() => {
            const a = parseLocalFloat(amount);
            const r = parseLocalFloat(interestRate) / 100;
            return isNaN(a) || isNaN(r) ? "" : (a * (1 - r)).toFixed(2);
          })()}
          InputProps={{ readOnly: true }}
        />
        <TextField
          label="Payout Date"
          type="date"
          fullWidth
          margin="dense"
          value={formatDateToYYYYMMDD(payoutDate)}
          InputProps={{ readOnly: true }}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="status-select-label">Status</InputLabel>
          <Select
            labelId="status-select-label"
            value={status}
            label="Status"
            onChange={e => setStatus(e.target.value)}
          >
            {payoutStatuses.map(s => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel id="payout-method-label">Payout Method</InputLabel>
          <Select
            labelId="payout-method-label"
            value={payoutMethod}
            label="Payout Method"
            onChange={e => setPayoutMethod(e.target.value)}
          >
            {methodOptions.map(method => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          label="BlitzPay"
          control={
            <Switch
              checked={blitzPay}
              onChange={(e, checked) => setBlitzPay(checked)}
            />
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={mutation.isLoading}>
          {mutation.isLoading ? "Saving..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
